import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from "react";
import { newPlaylist } from "../../config/configHelpers";
import { Playlist, Playlists, RadioName, RadioState } from "../../config/audio/types";
import { PLAYLIST } from "../../config/audio/playlistConfig";
import { createPlaylistFromState, shuffleAudioTracksForPlaylist } from "../utils/audio/radio";
import { DEFAULT_RADIO_STATE } from "../../config/audio/radioConfig";
import { audioPlayer } from "../utils/audio";
import { CHROME_AUTOPLAY_EVENTS } from "../../config/globals";
import { LOGS } from "../utils/logging";

export const hasGestured = () => {
	return navigator.userActivation && navigator.userActivation.hasBeenActive;
};

export type AudioContextType = {
	radioState: Record<RadioName, RadioState>;
	setRadioState: Dispatch<SetStateAction<Record<RadioName, RadioState>>>;
	playSound: (playlistName: Playlists, gain: number) => void;
	currentPlaylists: Playlist[];
	setCurrentPlaylists: Dispatch<SetStateAction<Playlist[]>>;
	hasUserGestured: boolean;
};

const initialAudioContext: AudioContextType = {
	radioState: {
		GLOBAL: DEFAULT_RADIO_STATE,
		CABIN: DEFAULT_RADIO_STATE,
	},
	playSound: () => {},
	setRadioState: () => {},
	currentPlaylists: [],
	setCurrentPlaylists: () => {},
	hasUserGestured: false,
};

export const audioContext = createContext<AudioContextType>(initialAudioContext);

export function useUserGestureTriggered() {
	const [userHasBeenActive, setUserHasBeenActive] = useState(
		navigator.userActivation?.hasBeenActive ?? false,
	);

	useEffect(() => {
		if (userHasBeenActive) return;

		const handleGesture = () => setUserHasBeenActive(true);

		CHROME_AUTOPLAY_EVENTS.forEach((event) =>
			window.addEventListener(event, handleGesture, { once: true }),
		);

		return () => {
			CHROME_AUTOPLAY_EVENTS.forEach((event) =>
				window.removeEventListener(event, handleGesture),
			);
		};
	}, [userHasBeenActive]);

	return userHasBeenActive;
}

export function useAudioPlayerOnGesture(hasBeenActive: boolean) {
	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		if (!hasBeenActive || isInitialized) return;

		const initializeAudio = async () => {
			try {
				LOGS.AUDIO && console.log("Initializing audio player");
				await audioPlayer.initialize();
				setIsInitialized(true);
			} catch (error) {
				console.error("Failed to initialize audio player:", error);
			}
		};

		initializeAudio();
		return () => {}; // Cleanup handled by audioPlayer.destroy()
	}, [hasBeenActive, isInitialized]);

	return isInitialized;
}

export default function AudioContextProvider({ children }: { children: ReactNode }) {
	// browsers do not allow audio to play on page without user interaction
	const hasUserGestured = useUserGestureTriggered();
	const isAudioInitialized = useAudioPlayerOnGesture(hasUserGestured);

	const [currentPlaylists, setCurrentPlaylists] = useState<Playlist[]>([]);
	const [radioState, setRadioState] = useState<Record<RadioName, RadioState>>(
		initialAudioContext.radioState,
	);

	const playSound = (playlistName: Playlists, gain: number) => {
		if (!hasUserGestured || !isAudioInitialized) return;

		const playlist = newPlaylist(PLAYLIST[playlistName], gain, false, playlistName);
		LOGS.AUDIO && console.log("Playing one-shot sound", playlist);
		audioPlayer.playOneShot(playlist);
	};

	useEffect(() => {
		let cancelled = false;
		const runUpdate = async () => {
			if (!hasUserGestured || !isAudioInitialized || cancelled) {
				return;
			}

			// Combine enabled radio state with the current scene configuration data
			const enabledRadioStates = Object.entries(radioState)
				.map(([_, val]) => ({ ...val }))
				.filter((val) => val.isOn);

			const radioPlaylistsToAdd = enabledRadioStates
				.map(createPlaylistFromState)
				.map(shuffleAudioTracksForPlaylist);

			const nextPlaylists = [...currentPlaylists, ...radioPlaylistsToAdd];

			if (!cancelled) {
				await audioPlayer.updatePlaylists(nextPlaylists);
			}
		};

		runUpdate();

		return () => {
			cancelled = true;
		};
	}, [currentPlaylists, radioState, hasUserGestured, isAudioInitialized]);

	return (
		<audioContext.Provider
			value={{
				currentPlaylists,
				setCurrentPlaylists,
				playSound,
				radioState,
				setRadioState,
				hasUserGestured,
			}}
		>
			{children}
		</audioContext.Provider>
	);
}
